import type { ReactNode } from "react"
import { useEffect, useRef, useState } from "react"
import { useMsal } from "@azure/msal-react"
import { InteractionStatus } from "@azure/msal-browser"
import {
  acquireTokenSilent,
  instance,
} from "@/v2-backoffice-client/auth/auth.instance"
import { useIsAdvancedUser } from "@/v2-backoffice-client/auth/auth.groups"
import { useViewMode } from "@/v2-backoffice-client/settings/settings.config"

const OVERLAY_DELAY_MS = 500
const TOKEN_REFRESH_INTERVAL_MS = 1000 * 60 * 5

type AuthLoaderProps = {
  children: ReactNode
}

function AuthLoader(props: AuthLoaderProps) {
  const { children } = props
  const { inProgress } = useMsal()
  const [showOverlay, setShowOverlay] = useState(false)

  const isProcessing = inProgress !== InteractionStatus.None

  useEffect(() => {
    if (!isProcessing) {
      setShowOverlay(false)
      return
    }

    const timeout = setTimeout(() => {
      setShowOverlay(true)
    }, OVERLAY_DELAY_MS)

    return () => {
      clearTimeout(timeout)
    }
  }, [isProcessing])

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      // Signed out (sitting on the login page): nothing to refresh —
      // acquireTokenSilent would just throw "No active account" every
      // five minutes.
      if (instance.getAllAccounts().length === 0) return
      try {
        await acquireTokenSilent()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.debug("Token refresh failed:", error)
      }
    }

    checkAndRefreshToken()
    const interval = setInterval(
      checkAndRefreshToken,
      TOKEN_REFRESH_INTERVAL_MS,
    )

    return () => {
      return clearInterval(interval)
    }
  }, [])

  const isAdvancedUser = useIsAdvancedUser()
  const [, setViewMode] = useViewMode()
  const hasSyncedViewMode = useRef(false)

  useEffect(() => {
    if (isProcessing || hasSyncedViewMode.current) return
    hasSyncedViewMode.current = true
    setViewMode(isAdvancedUser ? "advanced" : "simple")
  }, [isProcessing, isAdvancedUser, setViewMode])

  return (
    <>
      {children}
      {showOverlay && (
        <div className="auth-loader-overlay">
          <div className="auth-loader-spinner" />
          Processing authentication...
        </div>
      )}
    </>
  )
}

export default AuthLoader
